import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useGetNursesQuery, useDeleteNurseMutation } from "../../../../../services/location_services";
import AddFacilityStaffDialog from "./AddFacilityStaffDialog";
import FacilityListing from "./facilityListing";
import Accordion from "../../../../../components/accordion";
import { BtnComponent } from "../../../../../components/button";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { useForm } from "react-hook-form";
import { useGroupChatContext } from "../../../../../context/groupchatContext";
import MainPageDialog from "../../../../../components/dialog";
import LocationCheckin from "../../../../locationCheckin";
import ActionDialog from "../../../../../components/actionDialog";
import { toast } from "react-toastify";

const FACILITY_STAFF_RECORDS_PER_PAGE = 20;

function FacilityStaff() {

    const { authData } = useGroupChatContext();

    // states
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedNurse, setSelectedNurse] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isOpenCheckInPopup, setIsOpenCheckInPopup] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, nurse: null });

    const [deleteNurseTrigger, { isLoading: deletingNurse }] = useDeleteNurseMutation();
    
    // form management
    const { control, reset, getValues } = useForm({
        defaultValues: {
            nurse_name: "",
        },
    });

    const nurseQueryParams = useMemo(() => {
        return {
            ...(searchKeyword ? { keyword: searchKeyword } : {}),
            page: currentPage,
            start: (currentPage - 1) * FACILITY_STAFF_RECORDS_PER_PAGE,
            limit: FACILITY_STAFF_RECORDS_PER_PAGE,
        };
    }, [currentPage, searchKeyword]);

    // triggers - API will be called with keyword and pagination params when either changes
    const { data: nursesData, isLoading: nursesLoading, isFetching: nursesFetching, refetch: refetchNurses } = useGetNursesQuery(
        nurseQueryParams,
        { skip: false }
    );

    const nursesMeta = nursesData?.meta || {};

    useEffect(() => {
        if (nursesMeta?.pageCount && currentPage > nursesMeta.pageCount) {
            setCurrentPage(nursesMeta.pageCount);
        }
    }, [currentPage, nursesMeta?.pageCount]);

    const handleSearch = () => {
        const formData = getValues();
        const keyword = formData?.nurse_name?.trim() || "";
        setCurrentPage(1);
        setSearchKeyword(keyword);
    };

    const handleReset = () => {
        reset({
            nurse_name: "",
        });
        setCurrentPage(1);
        setSearchKeyword("");
    };

    const handleEdit = (nurse) => {
        setSelectedNurse(nurse);
        setShowAddDialog(true);
    };

    const handleAdd = () => {
        setSelectedNurse(null);
        setShowAddDialog(true);
    };

    const handleCheckInOut = (nurse) => {
        const userData = {
            user_id: nurse?.id,
            name: nurse?.name || nurse?.username,
            ...nurse,
        };
        setCurrentUser(userData);
        setIsOpenCheckInPopup(true);
    };

    const handleDeleteClick = useCallback((nurse) => {
        setDeleteConfirm({ open: true, nurse });
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        const nurse = deleteConfirm.nurse;
        if (!nurse?.id) return;
        try {
            const res = await deleteNurseTrigger({
                id: nurse.id,
                active: 0,
            }).unwrap();
            if (res?.code == 0) {
                toast.success(res?.msg || "Nurse deleted successfully");
                refetchNurses();
            } else {
                toast.error(res?.msg || "Failed to delete nurse");
            }
        } catch (err) {
            toast.error(err?.data?.msg || "Failed to delete nurse");
        } finally {
            setDeleteConfirm({ open: false, nurse: null });
        }
    }, [deleteConfirm.nurse, deleteNurseTrigger, refetchNurses]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteConfirm({ open: false, nurse: null });
    }, []);

    return (
        <div>
            {showAddDialog ? (
                <AddFacilityStaffDialog
                    setShowAddDialog={setShowAddDialog}
                    refetchNurses={refetchNurses}
                    selectedNurse={selectedNurse}
                    setSelectedNurse={setSelectedNurse}
                />
            ) : null}

            {isOpenCheckInPopup ? (
                <MainPageDialog
                    title={`New Check In or Check Out (${currentUser?.name || ""})`}
                    onCancelHandler={() => {
                        setIsOpenCheckInPopup(false);
                        setCurrentUser(null);
                    }}
                    component={
                        <LocationCheckin
                            closeLocationApptPopup={() => {
                                setIsOpenCheckInPopup(false);
                                setCurrentUser(null);
                            }}
                            fromWhere="nurses_listing"
                            currentUser={currentUser}
                            refetchData={refetchNurses}
                        />
                    }
                    footer={false}
                />
            ) : null}

            <div className="py-2 pl-3 pr-[1.7rem]">
                <Accordion
                    title="Facility Staff Search"
                    open={false}
                    showDialogue={true}
                    rightDialogHandler={handleAdd}
                    size="sm"
                    icon={
                        <BtnComponent
                            text="Add"
                            btnType={authData?.is_provider ? 'primary' : 'primary_theme'}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAdd();
                            }}
                        />
                    }
                    iconTooltip="Add Facility Staff"
                >
                <div className="pt-4">
                    <div className="row flex gap-x-4 pb-3 w-full">
                        <div className="w-1/2 md:w-1/4">
                            <InputField
                                title="Nurse Name"
                                name="nurse_name"
                                control={control}
                                placeholder=""
                            />
                        </div>
                        <div className="w-1/4 flex items-end">
                            <BtnComponent 
                                text="Search" 
                                btnType="primary" 
                                onClick={handleSearch}
                            />
                            <BtnComponent 
                                className="ml-5"
                                text="Reset" 
                                btnType="delete" 
                                onClick={handleReset}
                            />
                        </div>
                    </div>
                </div>
                </Accordion>
            </div>
            <FacilityListing
                nurses={nursesData?.data}
                nursesLoading={nursesLoading || nursesFetching || false}
                onEdit={handleEdit}
                onCheckInOut={handleCheckInOut}
                onDelete={handleDeleteClick}
                deletingNurse={deletingNurse}
                pagination={{
                    currentPage,
                    onPageChange: setCurrentPage,
                    pageSize: nursesMeta?.limit || FACILITY_STAFF_RECORDS_PER_PAGE,
                    totalRecords: nursesMeta?.count || nursesMeta?.total || 0,
                }}
            />

            <ActionDialog
                showModal={deleteConfirm.open}
                setShowModal={handleDeleteCancel}
                title="Are you sure you want to delete the nurse from all locations?"
                onCancelHandler={handleDeleteCancel}
                onConfirmHandler={handleDeleteConfirm}
                confirmButtonText="Yes"
                cancelButtonText="No"
            >
            </ActionDialog>
        </div>
    );
}

export default FacilityStaff;
